import React, { useEffect, useState } from 'react';
import { Card, Col, Input, Row, Typography, Spin, Tooltip } from 'antd';
import { baseurl } from '../../components/lib/settings';
import { format } from 'date-fns';
import './AdminAmountReportsByDelegationCompany.css';
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Meta } = Card;
const { Search } = Input;
const { Title } = Typography;

function AdminAmountReportsByDelegationCompany() {
  const [groupedData, setGroupedData] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      const resp = await baseurl.post("v3/ngoma/administrator/financing/by_editions/");
      const data = resp.data;
      const groupedByYear = data.reduce((acc, item) => {
        const year = new Date(item.first_vote_date).getFullYear();
        if (!acc[year]) acc[year] = [];
        acc[year].push(item);
        return acc;
      }, {});

      setGroupedData(groupedByYear);
      setFilteredData(groupedByYear);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 2000);
    return () => clearInterval(intervalId);
  }, []);

  const formatToMetical = (amount) => {
    return amount.toLocaleString('pt-BR', { style: 'currency', currency: 'MZN' });
  };

  const formatStartDate = (formatDate) => {
    return format(new Date(formatDate), "dd/MM/yyyy");
  };

  const formatEndDate = (formatDate) => {
    return format(new Date(formatDate), "dd/MM/yyyy");
  };

  const handleSearch = (value) => {
    setSearchTerm(value.toLowerCase());
    const filtered = {};
    Object.keys(groupedData).forEach(year => {
      filtered[year] = (groupedData[year] || []).filter(item =>
        item.delegation_description && item.delegation_description.toLowerCase().includes(value.toLowerCase())
      );
    });
    setFilteredData(filtered);
  };

  return (
    <div className='query-query-container'>
      <div className='header-sticky'>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          <Title
            className='total-acumulado'
            level={2}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              margin: '0',
            }}
          >
            Total acumulado por delegação
          </Title>
          <Search
            placeholder="Pesquisar nome da delegação"
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              maxWidth: '300px',
            }}
          />
        </div>
      </div>

      <span style={{fontSize: '16px'}} className='warning-info'>* Os dados são agrupados em ordem decrescente do valor total acumulado por cada delegação.</span>
            <br/>
            <br/>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 40,
                }}
                spin
              />
            }
          />
        </div>
      ) : (
        <>
          {Object.keys(filteredData).sort((a, b) => b - a).map(year => {
            const sortedItems = [...filteredData[year]].sort(
              (a, b) => (b.delegation_total_amount || 0) - (a.delegation_total_amount || 0)
            );

            const yearTotal = sortedItems.reduce((acc, item) => acc + (item.delegation_total_amount || 0), 0);

            // Calcular totais por operadora
            const mpesaTotal = sortedItems.reduce((acc, item) => acc + (item.operator_totals?.mpesa || 0), 0);
            const emolaTotal = sortedItems.reduce((acc, item) => acc + (item.operator_totals?.emola || 0), 0);
            const mkeshTotal = sortedItems.reduce((acc, item) => acc + (item.operator_totals?.mkesh || 0), 0);

            return (
              <div key={year} className="year-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  {/* Lado esquerdo: Ano + Status de pagamento */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <h2 style={{ margin: 0, marginBottom: '5px' }}>{year}</h2>
                    <div style={{ display: 'flex', gap: '15px', fontSize: '14px', alignItems: 'center' }}>
                      {/* Status de pagamento */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {/* <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: 'green',
                          display: 'inline-block'
                        }} /> */}
                        <span>
                           <CheckCircleOutlined style={{ color: 'green', fontSize: '15px' }} /> Desembolsado
                        </span>
                      </div>|
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>
                          <CloseCircleOutlined style={{ color: 'red', fontSize: '15px' }} /> Por desembolsar 
                        </span>
                      </div>|
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>
                          {'<24h:'} Últimas 24 horas
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lado direito: Total + Operadoras */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '20px', marginBottom: '5px' }}>
                      {`Total: ${formatToMetical(yearTotal)}`}
                    </span>
                    <div style={{ display: 'flex', gap: '10px', fontSize: '14px', alignItems: 'center' }}>
                      {/* Operadoras */}
                      <span>M-Pesa: {formatToMetical(mpesaTotal)}</span>
                      <b>|</b>
                      <span>E-mola: {formatToMetical(emolaTotal)}</span>
                    </div>
                  </div>
                </div>
                <Card style={{ backgroundColor: "#eeeeee", marginBottom: "20px" }}>
                  <Row gutter={[24, 24]}>
                    {sortedItems.map((item, index) => (
                      <Col key={index} xs={24} sm={12} md={8} lg={6}>
                        <Card
                          hoverable
                          title={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Montserrat, sans-serif' }}>
                              <span className="description-limited">{item.delegation_description || "Descrição não disponível"}</span>
                              <Tooltip title={item.is_active ? "Em curso" : "Encerrado"}>
                                <span
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: item.is_active ? 'green' : 'red',
                                    display: 'inline-block',
                                    marginLeft: '10px',
                                  }}
                                />
                              </Tooltip>
                            </div>
                          }
                          style={{ width: "100%" }}
                          cover={
                            <div>
                              <div style={{
                                marginLeft: '25px',
                                // padding: '5px 0',
                              }}>
                                <span className="description-limited">{item.edition_description || "Descrição não disponível"}</span>
                              </div>
                              <div className="date-cover">
                                De {formatStartDate(item.first_vote_date)} a {formatEndDate(item.last_vote_date)}
                                <div className="operator-totals">
                                  <p>&nbsp;&nbsp;M-Pesa: {formatToMetical(item.operator_totals?.mpesa || 0)}</p>
                                  <p>&nbsp;&nbsp;E-mola: {formatToMetical(item.operator_totals?.emola || 0)}</p>
                                  <p>&nbsp;&nbsp;{'<24h'}: {formatToMetical(item.delegation_last_24h_amount || 0)}</p>

                                </div>
                              </div>
                            </div>
                          }
                        >
                          {/* <Meta className='moneyCss'
                            description={`Total: ${formatToMetical(item.delegation_total_amount || 0)}`}
                          /> */}
                          <Meta 
                            className='moneyCss'
                            description={
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                fontFamily: 'Montserrat, sans-serif'
                              }}>
                                <span style={{ 
                                  // fontWeight: 'bold', 
                                  // fontSize: '16px'
                                }}>
                                  Total: {formatToMetical(item.delegation_total_amount || 0)}
                                </span>

                                <Tooltip title={item.edition_disbursement ? "Valor já foi pago" : "Valor ainda não foi pago"}>
                                  <span style={{ marginLeft: '10px' }}>
                                    {item.edition_disbursement ? (
                                      <CheckCircleOutlined style={{ color: 'green', fontSize: '16px' }} />
                                    ) : (
                                      <CloseCircleOutlined style={{ color: 'red', fontSize: '16px' }} />
                                    )}
                                  </span>
                                </Tooltip>
                              </div>
                            }
                          />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default AdminAmountReportsByDelegationCompany;